import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  SHIPMENT_STAGES,
  invalidateShipmentHistory,
  invalidateShipmentLists,
} from '#/entities/shipment'
import { useSaveCustomer } from '#/features/order-create'
import type {
  EditCustomerDefaults,
  EditCustomerPayload,
  LocationFormData,
  ShipmentFormData,
  VehiclesFormData,
} from '#/features/order-create'
import type { PaymentFormData } from '#/features/payment-edit'
import type { ShipmentDetail } from '#/shared/api/generated/shipment/models'
import {
  getGetShipmentApiV1ShipmentsShipmentIdGetQueryKey,
  useConvertShipmentApiV1ShipmentsShipmentIdConvertPost,
  useUpdateShipmentApiV1ShipmentsShipmentIdPatch,
} from '#/shared/api/generated/shipment/theCargoShipment'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { usPhoneDigits } from '#/shared/lib/format'
import { toast } from '#/shared/ui/sonner'
import { buildPaymentConvert, buildPaymentUpdate } from '#/widgets/payment-card'
import { buildShipmentUpdate } from '#/widgets/shipment-card'

import { useOpenShipmentPreview } from './use-open-shipment-preview'

function useUpdateShipmentMutation(shipmentId: string | undefined) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  return useUpdateShipmentApiV1ShipmentsShipmentIdPatch({
    mutation: {
      onSuccess: () => {
        if (shipmentId) {
          void queryClient.invalidateQueries({
            queryKey:
              getGetShipmentApiV1ShipmentsShipmentIdGetQueryKey(shipmentId),
          })
        }
        invalidateShipmentLists(queryClient)
        invalidateShipmentHistory(queryClient)
        toast.success(t('shipmentCard.updateSuccess'))
      },
      onError: (error) => {
        toast.error(getApiErrorDetail(error) ?? t('shipmentCard.updateError'))
      },
    },
  })
}

function useConvertShipmentMutation(onConverted?: () => void) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const openPreview = useOpenShipmentPreview()
  return useConvertShipmentApiV1ShipmentsShipmentIdConvertPost({
    mutation: {
      onSuccess: (response) => {
        invalidateShipmentLists(queryClient)
        const detail = response.data
        toast.success(
          t('table.tariff.convertedToQuote', { code: detail.code }),
          {
            action: {
              label: t('table.tariff.viewQuote'),
              onClick: () => openPreview(SHIPMENT_STAGES.QUOTE, detail.id),
            },
          },
        )
        onConverted?.()
      },
      onError: (error) => {
        toast.error(t('table.tariff.activateError'), {
          description: getApiErrorDetail(error) ?? undefined,
        })
      },
    },
  })
}

interface SectionSavers {
  onVehiclesSave: (data: VehiclesFormData) => Promise<void>
  onRouteSave: (data: {
    location: LocationFormData
    shipment: ShipmentFormData
  }) => Promise<void>
  onPaymentSave: (data: PaymentFormData) => Promise<void>
  onConvertToQuote: (data: PaymentFormData) => Promise<void>
  onNotesSave: (notes: { lbNote: string; cmNote: string }) => Promise<void>
}

export function useShipmentSectionSavers(
  shipment: ShipmentDetail,
  options?: { onConverted?: () => void },
): SectionSavers {
  const shipmentId = shipment.id
  const update = useUpdateShipmentMutation(shipmentId)
  const convert = useConvertShipmentMutation(options?.onConverted)

  return {
    onVehiclesSave: async (data) => {
      await update.mutateAsync({
        shipmentId,
        data: buildShipmentUpdate({ shipment, vehicles: data }),
      })
    },
    onRouteSave: async (data) => {
      await update.mutateAsync({
        shipmentId,
        data: buildShipmentUpdate({
          shipment,
          location: data.location,
          shipmentFields: data.shipment,
        }),
      })
    },
    onPaymentSave: async (data) => {
      await update.mutateAsync({ shipmentId, data: buildPaymentUpdate(data) })
    },
    onNotesSave: async ({ lbNote, cmNote }) => {
      const cm = cmNote.trim()
      const lb = lbNote.trim()
      await update.mutateAsync({
        shipmentId,
        data: {
          instructions: cm.length > 0 ? cm : null,
          loadboard_instructions: lb.length > 0 ? lb : null,
        },
      })
    },
    onConvertToQuote: async (data) => {
      await convert.mutateAsync({
        shipmentId,
        data: buildPaymentConvert(data, SHIPMENT_STAGES.QUOTE),
      })
    },
  }
}

/* eslint-disable complexity */
function buildCustomerDefaults(
  brief: ShipmentDetail['customer'] | undefined,
): EditCustomerDefaults['customer'] {
  return {
    firstName: brief?.first_name ?? '',
    lastName: brief?.last_name ?? '',
    company: brief?.company ?? '',
    companyPhone: usPhoneDigits(brief?.company_phone),
    email: brief?.email ?? '',
    email2: brief?.secondary_email ?? '',
    billingEmail: brief?.billing_email ?? '',
    phone: usPhoneDigits(brief?.phone),
    mobile: usPhoneDigits(brief?.secondary_phone),
    note: brief?.note ?? '',
  }
}
/* eslint-enable complexity */

interface CustomerEdit {
  defaults: EditCustomerDefaults
  isSaving: boolean
  onSubmit: (data: EditCustomerPayload) => Promise<void>
}

export function useCustomerEdit(
  shipment: ShipmentDetail,
  onDone: () => void,
): CustomerEdit {
  const { save, isSaving } = useSaveCustomer({
    shipmentId: shipment.id,
    customerId: shipment.customer?.id,
    originalEmail: shipment.customer?.email ?? undefined,
  })
  return {
    defaults: { customer: buildCustomerDefaults(shipment.customer) },
    isSaving,
    onSubmit: async (data) => {
      const ok = await save(data.customer)
      if (ok) onDone()
    },
  }
}
